import { FC, useState } from "react";
import {
  Modal,
  InputNumber,
  Input,
  Switch,
  Select,
  Button,
  Tag,
  Spin,
} from "antd";
import {
  CheckCircle,
  Clock,
  Users,
  AlertTriangle,
  Package,
  Calendar,
  User,
} from "lucide-react";
import dayjs from "dayjs";
import { useCompleteTask } from "./complete-task.store";
import { useCompleteTask as useCompleteTaskMutation } from "~/services/task";
import { useGetAllStaff } from "~/services/operation";
import { useGetSupplies } from "~/services/supply";
import { useNotification } from "~/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { TASK_KEYS } from "~/services/task";
import { capitalize } from "~/utils";
import { CompleteTaskPayload } from "~/types/task";

export const CompleteTaskModal: FC = () => {
  const { isOpen, task, closeCompleteTask } = useCompleteTask();
  const mutation = useCompleteTaskMutation();
  const { success, error } = useNotification();
  const qc = useQueryClient();

  // Form state
  const [actualHours, setActualHours] = useState<number | null>(null);
  const [completionNotes, setCompletionNotes] = useState("");
  const [selectedHelperIds, setSelectedHelperIds] = useState<string[]>([]);
  const [helperHours, setHelperHours] = useState<Record<string, number>>({});
  const [selectedSupplies, setSelectedSupplies] = useState<
    Array<{ supplyId: string; quantity: number }>
  >([]);
  const [redFlag, setRedFlag] = useState(false);
  const [redFlagNotes, setRedFlagNotes] = useState("");

  // Data queries
  const { data: staffData } = useGetAllStaff({ getAll: true, pageSize: 100 });
  const { data: suppliesData } = useGetSupplies({ pageSize: 200 });

  const resetForm = () => {
    setActualHours(null);
    setCompletionNotes("");
    setSelectedHelperIds([]);
    setHelperHours({});
    setSelectedSupplies([]);
    setRedFlag(false);
    setRedFlagNotes("");
  };

  const handleClose = () => {
    resetForm();
    closeCompleteTask();
  };

  const handleSubmit = () => {
    if (!task || actualHours === null) return;

    const payload: CompleteTaskPayload = {
      actualHours,
      completionNotes: completionNotes || undefined,
      redFlag,
      redFlagNotes: redFlag ? redFlagNotes || undefined : undefined,
      helperIds: selectedHelperIds.length > 0 ? selectedHelperIds : undefined,
      helperHours:
        Object.keys(helperHours).length > 0 ? helperHours : undefined,
      supplyItems:
        selectedSupplies.length > 0 ? selectedSupplies : undefined,
    };

    mutation.mutate(
      { taskId: task.id, payload },
      {
        onSuccess: () => {
          qc.invalidateQueries({ queryKey: [TASK_KEYS.TASKS] });
          success({
            message: "Task completed",
            description: redFlag
              ? "Task completed with red flag. The admin has been notified."
              : "Task has been marked as complete.",
          });
          handleClose();
        },
        onError: (err) => {
          console.error("Error completing task:", err);
          error({
            message: "Failed to complete task",
            description: "Please try again.",
          });
        },
      }
    );
  };

  if (!task) return null;

  // Filter out already-assigned staff from helper options
  const assigneeIds = task.assignees?.map((a) => a.id) || [];
  const availableHelpers =
    staffData?.items?.filter((s) => !assigneeIds.includes(s.id)) || [];

  const supplies = suppliesData?.items || [];

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={640}
      title={null}
      closeIcon={null}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Complete Task</h2>
            <p className="text-sm text-gray-500 mt-1">
              {task.taskRef} - {task.title}
            </p>
          </div>
          <Tag color="blue">{capitalize(task.taskType)}</Tag>
        </div>

        {/* Task Summary */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
          <div className="flex items-center gap-4">
            {task.startDate && (
              <span className="flex items-center gap-1 text-gray-600">
                <Calendar size={14} />
                {dayjs(task.startDate).format("MMM D, YYYY")}
              </span>
            )}
            {task.startTime && (
              <span className="flex items-center gap-1 text-gray-600">
                <Clock size={14} />
                {dayjs(`2025-01-01 ${task.startTime}`).format("h:mm A")}
              </span>
            )}
            {task.duration && (
              <span className="text-gray-600">
                Est. {task.duration}h
              </span>
            )}
          </div>
          {task.assignees && task.assignees.length > 0 && (
            <div className="flex items-center gap-1 text-gray-600">
              <User size={14} />
              Assigned:{" "}
              {task.assignees
                .map((a) =>
                  a.user
                    ? `${a.user.firstName || ""} ${a.user.lastName || ""}`.trim()
                    : "Staff"
                )
                .join(", ")}
            </div>
          )}
        </div>

        {/* Confirm Hours */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Clock size={14} className="inline mr-1" />
            Actual Hours Worked <span className="text-red-500">*</span>
          </label>
          <InputNumber
            className="w-full"
            min={0.25}
            max={24}
            step={0.25}
            value={actualHours}
            onChange={(v) => setActualHours(v)}
            placeholder="e.g. 2.5"
            addonAfter="hrs"
            size="large"
          />
        </div>

        {/* Who Helped */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Users size={14} className="inline mr-1" />
            Who Helped? (optional)
          </label>
          <Select
            mode="multiple"
            className="w-full"
            placeholder="Select additional staff who helped"
            value={selectedHelperIds}
            onChange={(ids) => setSelectedHelperIds(ids)}
            options={availableHelpers.map((s) => ({
              value: s.id,
              label: s.user
                ? `${s.user.firstName || ""} ${s.user.lastName || ""}`.trim()
                : s.id,
            }))}
            filterOption={(input, option) =>
              (option?.label as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
          />
          {selectedHelperIds.length > 0 && (
            <div className="mt-2 space-y-2">
              {selectedHelperIds.map((id) => {
                const staff = availableHelpers.find((s) => s.id === id);
                const name = staff?.user
                  ? `${staff.user.firstName || ""} ${staff.user.lastName || ""}`.trim()
                  : "Staff";
                return (
                  <div
                    key={id}
                    className="flex items-center justify-between bg-gray-50 rounded p-2"
                  >
                    <span className="text-sm">{name}</span>
                    <InputNumber
                      size="small"
                      min={0.25}
                      max={24}
                      step={0.25}
                      placeholder="Hours"
                      value={helperHours[id]}
                      onChange={(v) =>
                        setHelperHours((prev) => ({
                          ...prev,
                          [id]: v ?? 0,
                        }))
                      }
                      addonAfter="hrs"
                      className="w-32"
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Supplies Used */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Package size={14} className="inline mr-1" />
            Supplies Used (optional)
          </label>
          <Select
            className="w-full mb-2"
            placeholder="Add a supply..."
            showSearch
            filterOption={(input, option) =>
              (option?.label as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
            value={undefined}
            onChange={(supplyId) => {
              if (
                supplyId &&
                !selectedSupplies.find((s) => s.supplyId === supplyId)
              ) {
                setSelectedSupplies((prev) => [
                  ...prev,
                  { supplyId, quantity: 1 },
                ]);
              }
            }}
            options={supplies
              .filter(
                (s) => !selectedSupplies.find((ss) => ss.supplyId === s.id)
              )
              .map((s) => ({
                value: s.id,
                label: `${s.supplyRef ? s.supplyRef + " - " : ""}${s.name}`,
              }))}
          />
          {selectedSupplies.length > 0 && (
            <div className="space-y-2">
              {selectedSupplies.map((item) => {
                const supply = supplies.find((s) => s.id === item.supplyId);
                return (
                  <div
                    key={item.supplyId}
                    className="flex items-center justify-between bg-gray-50 rounded p-2"
                  >
                    <span className="text-sm">
                      {supply?.name || item.supplyId}
                    </span>
                    <div className="flex items-center gap-2">
                      <InputNumber
                        size="small"
                        min={1}
                        value={item.quantity}
                        onChange={(v) =>
                          setSelectedSupplies((prev) =>
                            prev.map((s) =>
                              s.supplyId === item.supplyId
                                ? { ...s, quantity: v ?? 1 }
                                : s
                            )
                          )
                        }
                        className="w-20"
                      />
                      <button
                        className="text-red-400 hover:text-red-600 text-xs"
                        onClick={() =>
                          setSelectedSupplies((prev) =>
                            prev.filter(
                              (s) => s.supplyId !== item.supplyId
                            )
                          )
                        }
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Completion Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Completion Notes (optional)
          </label>
          <Input.TextArea
            rows={3}
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            placeholder="Any notes about the completed work..."
          />
        </div>

        {/* Red Flag */}
        <div
          className={`rounded-lg border p-4 ${
            redFlag
              ? "bg-red-50 border-red-300"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle
                size={18}
                className={redFlag ? "text-red-500" : "text-gray-400"}
              />
              <span className="font-medium text-sm">
                Raise a Red Flag
              </span>
            </div>
            <Switch checked={redFlag} onChange={setRedFlag} />
          </div>
          {redFlag && (
            <div className="mt-3">
              <p className="text-xs text-red-600 mb-2">
                This will notify the admin who created this task so they can
                review the issue.
              </p>
              <Input.TextArea
                rows={3}
                value={redFlagNotes}
                onChange={(e) => setRedFlagNotes(e.target.value)}
                placeholder="Describe the issue..."
                className="border-red-200"
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2 border-t">
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={mutation.isPending}
            disabled={actualHours === null || actualHours <= 0}
            icon={<CheckCircle size={16} />}
            className="bg-green-600 hover:bg-green-700"
          >
            Complete Task
          </Button>
        </div>
      </div>
    </Modal>
  );
};
